import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { PageHeader, DisclaimerFooter } from "@/components/Bits";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { fmtUSD } from "@/lib/calc";
import { Plus, Trash2, BookOpen } from "lucide-react";
import { toast } from "sonner";

const EMPTY = {
  instrument: "",
  direction: "BUY",
  entry: "",
  sl: "",
  tp: "",
  lot: "",
  result_usd: "",
  result_pct: "",
  time: "",
  screenshot_url: "",
  comment: "",
};

export default function Journal() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const { data } = await api.get("/journal");
    setItems(data);
  };
  useEffect(() => {
    load();
  }, []);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const add = async () => {
    if (!form.instrument.trim()) {
      toast.error("Podaj instrument");
      return;
    }
    setBusy(true);
    try {
      const payload = {
        ...form,
        entry: form.entry === "" ? null : Number(form.entry),
        sl: form.sl === "" ? null : Number(form.sl),
        tp: form.tp === "" ? null : Number(form.tp),
        lot: form.lot === "" ? null : Number(form.lot),
        result_usd: form.result_usd === "" ? null : Number(form.result_usd),
        result_pct: form.result_pct === "" ? null : Number(form.result_pct),
      };
      await api.post("/journal", payload);
      toast.success("Transakcja dodana");
      setForm(EMPTY);
      setOpen(false);
      load();
    } finally {
      setBusy(false);
    }
  };

  const remove = async (id) => {
    await api.delete(`/journal/${id}`);
    setItems((prev) => prev.filter((i) => i.id !== id));
  };

  return (
    <div>
      <PageHeader
        title="Trading Journal"
        subtitle="Zapisuj swoje transakcje."
        right={
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button
                data-testid="add-trade-button"
                className="font-display font-semibold bg-gradient-to-r from-violet-700 to-violet-500 border-0 shadow-[0_0_18px_rgba(168,85,247,0.35)]"
              >
                <Plus size={16} className="mr-1" /> DODAJ TRANSAKCJĘ
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-demon-surface border-violet-900/40 text-white max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="font-display text-xl">Nowa transakcja</DialogTitle>
              </DialogHeader>
              <div className="grid sm:grid-cols-2 gap-3 mt-2">
                <div>
                  <Label className="text-xs text-demon-muted">Instrument</Label>
                  <Input data-testid="journal-instrument" value={form.instrument} onChange={set("instrument")} placeholder="EURUSD" className="mt-1 bg-black/50 border-violet-900/40 text-white" />
                </div>
                <div>
                  <Label className="text-xs text-demon-muted">Kierunek</Label>
                  <Select value={form.direction} onValueChange={(v) => setForm((f) => ({ ...f, direction: v }))}>
                    <SelectTrigger data-testid="journal-direction" className="mt-1 bg-black/50 border-violet-900/40 text-white">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-demon-surface2 border-violet-900/40 text-white">
                      <SelectItem value="BUY">BUY</SelectItem>
                      <SelectItem value="SELL">SELL</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {[
                  ["entry", "Entry"],
                  ["sl", "SL"],
                  ["tp", "TP"],
                  ["lot", "Lot"],
                  ["result_usd", "Wynik $"],
                  ["result_pct", "Wynik %"],
                  ["time", "Godzina"],
                  ["screenshot_url", "Screenshot (URL)"],
                ].map(([k, label]) => (
                  <div key={k}>
                    <Label className="text-xs text-demon-muted">{label}</Label>
                    <Input
                      data-testid={`journal-${k}`}
                      type={["time", "screenshot_url"].includes(k) ? "text" : "number"}
                      step="any"
                      value={form[k]}
                      onChange={set(k)}
                      className="mt-1 bg-black/50 border-violet-900/40 text-white font-mono"
                    />
                  </div>
                ))}
                <div className="sm:col-span-2">
                  <Label className="text-xs text-demon-muted">Komentarz</Label>
                  <Textarea data-testid="journal-comment" value={form.comment} onChange={set("comment")} className="mt-1 bg-black/50 border-violet-900/40 text-white" />
                </div>
              </div>
              <Button
                data-testid="journal-save-button"
                onClick={add}
                disabled={busy}
                className="mt-3 font-display font-semibold bg-gradient-to-r from-violet-700 to-violet-500 border-0"
              >
                ZAPISZ TRANSAKCJĘ
              </Button>
            </DialogContent>
          </Dialog>
        }
      />

      {items.length === 0 ? (
        <div className="glow-card rounded-2xl p-12 text-center text-demon-muted">
          <BookOpen className="mx-auto mb-3 text-violet-400/60" size={36} />
          Brak zapisanych transakcji. Dodaj pierwszą.
        </div>
      ) : (
        <div className="space-y-3">
          {items.map((it) => (
            <div key={it.id} data-testid={`journal-item-${it.id}`} className="glow-card rounded-xl p-4 flex flex-wrap items-center gap-4 font-mono text-sm">
              <span className={`px-2 py-1 rounded-md text-xs font-bold ${it.direction === "BUY" ? "bg-emerald-500/15 text-emerald-400" : "bg-red-500/15 text-red-400"}`}>
                {it.direction}
              </span>
              <span className="text-white font-semibold">{it.instrument}</span>
              <span className="text-demon-muted">Lot {it.lot ?? "—"}</span>
              <span className="text-demon-muted">E {it.entry ?? "—"}</span>
              <span className="text-red-400">SL {it.sl ?? "—"}</span>
              <span className="text-emerald-400">TP {it.tp ?? "—"}</span>
              {it.result_usd != null && (
                <span className={it.result_usd >= 0 ? "text-emerald-400" : "text-red-400"}>
                  {it.result_usd >= 0 ? "+" : ""}{fmtUSD(it.result_usd)}
                </span>
              )}
              {it.time && <span className="text-demon-muted">{it.time}</span>}
              {it.comment && <span className="text-demon-muted/80 italic truncate max-w-[200px]">"{it.comment}"</span>}
              <button
                data-testid={`journal-delete-${it.id}`}
                onClick={() => remove(it.id)}
                className="ml-auto text-red-400/70 hover:text-red-400"
              >
                <Trash2 size={16} />
              </button>
            </div>
          ))}
        </div>
      )}

      <DisclaimerFooter />
    </div>
  );
}
